import * as React from 'react';
import type { ButtonFullProps } from '../../common/Button/Button';
import Button from '../../common/Button/Button';
import type { TooltipPadding, TooltipPlacement, TooltipProps } from '../Tooltip/Tooltip';
import Tooltip from '../Tooltip/Tooltip';
import useUpdateRef from '../../../hooks/useUpdateRef';
import Closable from '../Closable';
import type { ReactRef } from '../../../modules/react-utils';
import { assignRefs } from '../../../modules/react-utils';
import cn from '~modules/cn';
import Styles from './dropdown-popup.module.scss';

export type DropdownPopupTriggerProps = React.HTMLAttributes<HTMLElement>;

export type DropdownPopupRenderTrigger = (
    triggerProps: DropdownPopupTriggerProps,
    opts: { isToggled: boolean; setToggled: (state: boolean) => void }
) => JSX.Element;

export type DropdownPopupContext = {
    togglePopup: (state: boolean) => void;
    isToggled: boolean;
};

export type DropdownPopupProps = Pick<TooltipProps, 'popperProps'> & {
    className?: string;
    tooltipClassName?: string;
    onToggle?: (state: boolean) => void;
    menuHost?: HTMLElement;
    contextRef?: ReactRef<DropdownPopupContext>;
    children?: React.ReactNode;
    offset?: number[];
    placement?: TooltipPlacement;
    padding?: TooltipPadding;
} & (
        | {
              buttonProps?: Partial<ButtonFullProps>;
          }
        | {
              renderTrigger?: DropdownPopupRenderTrigger;
          }
    );

const DropdownPopup = React.forwardRef<HTMLDivElement, DropdownPopupProps>((props, ref) => {
    const [isToggled, setToggled] = React.useState(false);

    const onToggleRef = useUpdateRef(props.onToggle);
    const triggerRef = React.useRef(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (props.contextRef) {
            assignRefs(props.contextRef)({
                togglePopup: setToggled,
                isToggled
            });
        }
        onToggleRef.current?.(isToggled);
    }, [isToggled, onToggleRef, props.contextRef]);

    React.useEffect(() => {
        if (isToggled) {
            // Allow keyboard users to enter the popup if it contains interactive elements
            const firstInteractiveItem = contentRef.current?.querySelector('button, a') as HTMLButtonElement | HTMLLinkElement | null;
            firstInteractiveItem?.focus();
        }
    }, [isToggled]);

    const buttonProps = 'buttonProps' in props ? props.buttonProps : undefined;
    const renderTrigger = 'renderTrigger' in props ? props.renderTrigger : undefined;
    const triggerProps: Partial<ButtonFullProps> & { ref?: React.Ref<HTMLButtonElement> } = {
        ref: triggerRef,
        'aria-expanded': isToggled,
        onClick(e) {
            setToggled(!isToggled);
            buttonProps?.onClick?.(e);
        }
    };

    return (
        <div className={props.className} ref={ref}>
            {renderTrigger?.(triggerProps, { isToggled, setToggled }) ?? (
                <Button type="button" buttonType="secondary" active={isToggled} {...buttonProps} {...triggerProps} />
            )}
            {isToggled && (
                <Tooltip
                    className={cn(Styles.tooltip, props.tooltipClassName)}
                    for={triggerRef}
                    appearanceClassNames={['tooltip--no-background', 'tooltip--no-max-width', 'tooltip--box-shadow', 'tooltip--no-padding']}
                    tooltipProps={{
                        trigger: 'click',
                        visible: true
                    }}
                    offset={props.offset}
                    padding={props.padding}
                    placement={props.placement}
                    host={props.menuHost}
                    popperProps={props.popperProps}
                    appendParent
                >
                    <Closable
                        onClose={() => {
                            setToggled(false);
                        }}
                        includeElements={[triggerRef]}
                    >
                        <div ref={contentRef}>{props.children}</div>
                    </Closable>
                </Tooltip>
            )}
        </div>
    );
});

DropdownPopup.displayName = 'DropdownPopup';

export default DropdownPopup;
